import React, { useMemo } from "react";
import {
  buildFacility360Metrics,
  getFacility360ShiftStart,
  getFacility360ShiftRole,
  facility360ToDate,
  normalizeFacility360Value,
} from "../utils/facility360Metrics";

function formatMoney(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(value) || 0);
}

function formatDate(value) {
  const date = facility360ToDate(value);

  if (!date) return "Date unavailable";

  return date.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function titleCase(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function MetricCard({
  label,
  value,
  color = "#0f172a",
}) {
  return (
    <div
      style={{
        padding: 13,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
      }}
    >
      <div
        style={{
          color: "#64748b",
          fontSize: 10,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {label}
      </div>

      <div
        style={{
          marginTop: 6,
          color,
          fontSize: 20,
          fontWeight: 950,
        }}
      >
        {value}
      </div>
    </div>
  );
}

export default function Facility360Financial({
  facility,
  shifts = [],
  showMargin = true,
}) {
  const facilityId =
    normalizeFacility360Value(
      facility?.id || facility?.facilityId
    );

  const sharedMetrics = useMemo(
    () =>
      buildFacility360Metrics({
        facility,
        shifts,
        users: [],
      }),
    [facility, shifts]
  );

  const {
    financialRows,
    financialSummary: summary,
  } = sharedMetrics;

  const marginPercent =
    summary.marginPercent;

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Financial
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          Completed-shift billing and payroll for{" "}
          {facility?.name ||
            facility?.facilityName ||
            facility?.id ||
            "this facility"}
          .
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(2, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        <MetricCard
          label="Revenue"
          value={formatMoney(summary.revenue)}
          color="#166534"
        />

        <MetricCard
          label="Payroll Cost"
          value={formatMoney(summary.payroll)}
          color="#b45309"
        />

        {showMargin ? (
          <>
            <MetricCard
              label="Gross Margin"
              value={formatMoney(summary.margin)}
              color={
                summary.margin >= 0
                  ? "#166534"
                  : "#b91c1c"
              }
            />

            <MetricCard
              label="Margin %"
              value={`${marginPercent}%`}
              color={
                marginPercent >= 20
                  ? "#166534"
                  : "#b45309"
              }
            />
          </>
        ) : null}

<MetricCard
  label="Pending Revenue"
  value={formatMoney(
    summary.pendingRevenue
  )}
  color="#b45309"
/>

<MetricCard
  label="Paid Revenue"
  value={formatMoney(
    summary.paidRevenue
  )}
  color="#166534"
/>
        <MetricCard
          label="Completed Shifts"
          value={financialRows.length}
          color="#7c3aed"
        />
      </div>

      {financialRows.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            textAlign: "center",
            color: "#64748b",
            background: "#f8fafc",
            border: "1px dashed #cbd5e1",
            fontWeight: 750,
          }}
        >
          No completed shifts are available for billing.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          <div
            style={{
              color: "#0f172a",
              fontSize: 15,
              fontWeight: 950,
            }}
          >
            Shift Financial History
          </div>

          {financialRows.map((row, index) => {
            const shift = row.shift;

            const invoiceStatus =
              normalizeFacility360Value(
                shift?.invoiceStatus ||
                  "pending"
              );

            const paid =
              invoiceStatus === "paid";

            return (
              <div
                key={
                  shift.id ||
                  shift.shiftId ||
                  `${facilityId}-${index}`
                }
                style={{
                  padding: 13,
                  borderRadius: 14,
                  background: "#ffffff",
                  border:
                    "1px solid #e2e8f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent:
                      "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 950,
                      }}
                    >
                      {getFacility360ShiftRole(
                        shift
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#64748b",
                        fontSize: 11,
                        fontWeight: 750,
                      }}
                    >
                      {formatDate(
                        getFacility360ShiftStart(
                          shift
                        )
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: 5,
                        color: "#475569",
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {row.hours.toFixed(1)} hours
                      {" × "}
                      {formatMoney(row.billRate)}
                      /hr
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        color: "#64748b",
                        fontSize: 10,
                        fontWeight: 700,
                      }}
                    >
                      Payroll:{" "}
                      {formatMoney(row.payroll)}
                      {showMargin
                        ? ` · Margin: ${formatMoney(
                            row.margin
                          )}`
                        : ""}
                    </div>
                  </div>

                  <div
                    style={{
                      textAlign: "right",
                    }}
                  >
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 15,
                        fontWeight: 950,
                      }}
                    >
                      {formatMoney(row.revenue)}
                    </div>

                    <div
                      style={{
                        display: "inline-block",
                        marginTop: 5,
                        padding: "5px 8px",
                        borderRadius: 999,
                        color: paid
                          ? "#166534"
                          : "#92400e",
                        background: paid
                          ? "#dcfce7"
                          : "#fef3c7",
                        fontSize: 9,
                        fontWeight: 950,
                      }}
                    >
                      {titleCase(invoiceStatus)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}